/**
 * overlayPrefs — shared, persisted visibility prefs for the video overlays, so the
 * toggles in Setup > Display & Theme AND the in-view overlay control both drive the
 * SAME state, and every already-streaming player + the camera-info overlay updates
 * live (no reload). Applies to all product modes (local/eview/cloud).
 *
 * Keys:
 *   info     — camera name + timestamp text (UI label "Name & Timestamp"), default ON
 *   objects  — AI object-detection boxes + labels, default ON
 *   lpr      — license-plate boxes + plate text, default ON
 *   metadata — FPS/bitrate/codec/resolution readout (UI label "Camera Info", Java parity), default OFF
 *   recBox   — recording/alert box (L-I2), default OFF; reserved, no UI toggle yet
 *
 * Backed by localStorage; setOverlayPref broadcasts a snapshot to every subscriber.
 */
const KEY = 'dview-overlay-prefs';
// info/objects/lpr default ON (the standard live overlays, also toggled from Setup > Display).
// metadata/recBox default OFF. metadata is the diagnostic FPS/bitrate readout — the "Camera overlays"
// gear (desktop) + the mobile/tablet overlays sheet surface it as "Camera Info" (#86bb5dx62, Mock D4).
// recBox is the recording/alert box: its render is L-I2 (still parked) and it has no toggle in the UI
// yet, but the pref persists ready to drive it.
const DEFAULTS = { info: true, objects: true, lpr: true, metadata: false, recBox: false };
const subscribers = new Set();

// Back-compat: the old single 'dview-show-live-overlay' flag seeded `info` only.
function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (raw && typeof raw === 'object') return { ...DEFAULTS, ...raw };
  } catch (e) { /* corrupt/absent → fall through to legacy/defaults */ }
  const legacy = localStorage.getItem('dview-show-live-overlay');
  return { ...DEFAULTS, info: legacy !== 'false' };
}

let prefs = load();

/** Current prefs snapshot (a copy — callers can't mutate the shared object). */
export function getOverlayPrefs() {
  return { ...prefs };
}

/** Set one overlay key; persists + broadcasts to subscribers. */
export function setOverlayPref(key, value) {
  if (!(key in DEFAULTS)) return;
  prefs = { ...prefs, [key]: !!value };
  try { localStorage.setItem(KEY, JSON.stringify(prefs)); } catch (e) { /* private mode / quota — keep in-memory */ }
  const snapshot = getOverlayPrefs();
  subscribers.forEach((cb) => { try { cb(snapshot); } catch (e) { /* one bad subscriber can't break the rest */ } });
}

/** Subscribe to pref changes; returns an unsubscribe fn (safe for useEffect cleanup). */
export function subscribeOverlayPrefs(cb) {
  subscribers.add(cb);
  return () => { subscribers.delete(cb); };
}
